// src/store/slices/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AUTH_TOKEN } from 'constants/AuthConstant';
import AuthService from 'services/AuthService';

// ── Helpers de persistencia ──────────────────────────────────────
const getStoredUser = () => {
	try {
		return JSON.parse(localStorage.getItem('user')) || null;
	} catch {
		return null;
	}
};

export const initialState = {
	loading: false,
	message: '',
	showMessage: false,
	redirect: '',
	token: localStorage.getItem(AUTH_TOKEN) || null,
	user: getStoredUser(),
	role: getStoredUser()?.role || null,
};

// ── Thunks ───────────────────────────────────────────────────────
export const signIn = createAsyncThunk('auth/login', async (data, { rejectWithValue }) => {
	try {
		const response = await AuthService.login(data);
		const token = response.access_token;
		const user = response.user;

		localStorage.setItem(AUTH_TOKEN, `Bearer ${token}`);
		localStorage.setItem('user', JSON.stringify(user));

		return { token, user };
	} catch (err) {
		return rejectWithValue(
			err.response?.data?.message || 'Error al iniciar sesión'
		);
	}
});

export const signOut = createAsyncThunk('auth/logout', async () => {
	try {
		await AuthService.logout();
	} catch (_) { /* ignorar error de red */ }
	localStorage.removeItem(AUTH_TOKEN);
	localStorage.removeItem('user');
});

export const fetchMe = createAsyncThunk('auth/me', async (_, { rejectWithValue }) => {
	try {
		const response = await AuthService.me();
		const user = response.user;
		localStorage.setItem('user', JSON.stringify(user));
		return user;
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || 'Error');
	}
});

// ── Slice ────────────────────────────────────────────────────────
export const authSlice = createSlice({
	name: 'auth',
	initialState,

	reducers: {
		authenticated: (state, action) => {
			state.loading = false;
			state.redirect = '/';
			state.token = action.payload;
		},
		showAuthMessage: (state, action) => {
			state.message = action.payload;
			state.showMessage = true;
			state.loading = false;
		},
		hideAuthMessage: (state) => {
			state.message = '';
			state.showMessage = false;
		},
		signOutSuccess: (state) => {
			state.loading = false;
			state.token = null;
			state.user = null;
			state.role = null;
			state.redirect = '/';
		},
		showLoading: (state) => {
			state.loading = true;
		},
		signInSuccess: (state, action) => {
			state.loading = false;
			state.token = action.payload;
		},
	},

	extraReducers: (builder) => {
		builder
			// signIn
			.addCase(signIn.pending, (state) => {
				state.loading = true;
			})
			.addCase(signIn.fulfilled, (state, action) => {
				state.loading = false;
				state.redirect = '/';
				state.token = action.payload.token;
				state.user = action.payload.user;
				state.role = action.payload.user?.role || null;
			})
			.addCase(signIn.rejected, (state, action) => {
				state.message = action.payload;
				state.showMessage = true;
				state.loading = false;
			})
			// signOut
			.addCase(signOut.fulfilled, (state) => {
				state.loading = false;
				state.token = null;
				state.user = null;
				state.role = null;
				state.redirect = '/';
			})
			.addCase(signOut.rejected, (state) => {
				state.loading = false;
				state.token = null;
				state.user = null;
				state.role = null;
				state.redirect = '/';
			})
			// Dentro de extraReducers, después del último .addCase existente:
			.addCase(signUp.pending, (state) => { state.loading = true; })
			.addCase(signUp.fulfilled, (state) => { state.loading = false; })
			.addCase(signUp.rejected, (state, action) => {
				state.message = action.payload;
				state.showMessage = true;
				state.loading = false;
			})
			.addCase(signInWithGoogle.pending, (state) => { state.loading = true; })
			.addCase(signInWithGoogle.fulfilled, (state) => { state.loading = false; })
			.addCase(signInWithGoogle.rejected, (state, action) => {
				state.message = action.payload;
				state.showMessage = true;
				state.loading = false;
			})
			.addCase(signInWithFacebook.pending, (state) => { state.loading = true; })
			.addCase(signInWithFacebook.fulfilled, (state) => { state.loading = false; })
			.addCase(signInWithFacebook.rejected, (state, action) => {
				state.message = action.payload;
				state.showMessage = true;
				state.loading = false;
			})
			// fetchMe
			.addCase(fetchMe.fulfilled, (state, action) => {
				state.user = action.payload;
				state.role = action.payload?.role || null;
			});


	},
});

export const {
	authenticated,
	showAuthMessage,
	hideAuthMessage,
	signOutSuccess,
	showLoading,
	signInSuccess,
} = authSlice.actions;

// ── Selectores ───────────────────────────────────────────────────
export const selectToken = (state) => state.auth.token;
export const selectUser = (state) => state.auth.user;
export const selectRole = (state) => state.auth.role;
export const selectIsAdmin = (state) => state.auth.role === 'admin';
export const selectIsSuperAdmin = (state) =>
	state.auth.role === 'admin' && !state.auth.user?.empresa_id;
export const signUp = createAsyncThunk('auth/register', async (_, { rejectWithValue }) => {
	return rejectWithValue('Registro no disponible. Contacta al administrador.');
});

export const signInWithGoogle = createAsyncThunk('auth/signInWithGoogle', async (_, { rejectWithValue }) => {
	return rejectWithValue('Login con Google no disponible.');
});

export const signInWithFacebook = createAsyncThunk('auth/signInWithFacebook', async (_, { rejectWithValue }) => {
	return rejectWithValue('Login con Facebook no disponible.');
});

export default authSlice.reducer;